import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { StatsCard } from "@/components/homepage/StatsCard";
import { Wrench, BarChart3, FileText } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

interface StatsSectionProps {
  toolsCount: number;
  generationsThisMonth: number;
  questionsCount: number;
  user: any;
  prefix: string;
  loading?: boolean;
  generationsTrend?: { value: number; direction: "up" | "down" };
  questionsTrend?: { value: number; direction: "up" | "down" };
}

export function StatsSection({ toolsCount, generationsThisMonth, questionsCount, user, prefix, loading, generationsTrend, questionsTrend }: StatsSectionProps) {
  const { t } = useTranslation();

  return (
    <div className="relative mt-6 z-10">
      <div className="max-w-6xl mx-auto px-4">
        <div className="grid gap-4 md:grid-cols-3 mb-6">
          {loading ? (
            Array.from({ length: 3 }).map((_, idx) => (
              <div key={idx} className="p-6 border rounded-lg bg-white">
                <div className="flex items-center gap-4">
                  <Skeleton className="h-10 w-10 rounded-xl" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-6 w-24" />
                    <Skeleton className="h-4 w-20" />
                  </div>
                </div>
                <div className="mt-4 space-y-2">
                  <Skeleton className="h-8 w-28" />
                  <Skeleton className="h-3 w-16" />
                </div>
              </div>
            ))
          ) : (
            <>
              <StatsCard
                value={toolsCount}
                label={t('homepage.stats.toolsAvailable', 'Tools available')}
                icon={Wrench}
                gradient
                // trend={{ value: 18, direction: 'up' }}
                ariaLabel={t('homepage.stats.toolsAria', 'Total tools available')}
              />
              <StatsCard
                value={generationsThisMonth ?? 0}
                label={t('homepage.stats.generationsThisMonth', 'Generations this month')}
                icon={BarChart3}
                trend={generationsTrend}
                ariaLabel={t('homepage.stats.generationsAria', 'Generations created this month')}
              />
              <StatsCard
                value={questionsCount ?? 0}
                label={t('homepage.stats.questionsAvailable', 'Questions available')}
                icon={FileText}
                trend={questionsTrend}
                ariaLabel={t('homepage.stats.questionsAria', 'Total questions available')}
              />
            </>
          )}
        </div>

        {!user && (
          <div className="text-center text-sm text-muted-foreground mb-8">
            {t('homepage.stats.ctaSignup', 'Get free access to all tools - sign up now in 30 seconds')}{' '}
            <Link to={`${prefix}/auth`} className="story-link font-medium text-primary">
              {t('nav.signIn', 'Sign In')}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
} 